import { useState, useEffect, useCallback } from "react";
import { useConfirm } from "../../hooks/useConfirm";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

function GeneratedImages() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const token = localStorage.getItem("anna_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/generated-images`, { headers });
      if (!res.ok) throw new Error(`Failed to load images: ${res.status}`);
      const data = await res.json();
      setFiles(data.files || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDelete = async (s3Key) => {
    if (!(await confirm("Delete this generated image?"))) return;
    setDeletingKey(s3Key);
    try {
      const encodedKey = encodeURIComponent(s3Key);
      const res = await fetch(`${API_BASE}/generated-images/${encodedKey}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      setFiles((prev) => prev.filter((f) => f.s3Key !== s3Key));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingKey(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return <div className="files-loading">Loading generated images...</div>;
  }

  if (error) {
    return (
      <div className="files-error">
        <p>{error}</p>
        <button className="btn-primary" onClick={fetchFiles}>Retry</button>
      </div>
    );
  }

  if (files.length === 0) {
    return <div className="files-empty">No generated images yet. Ask Anna to generate an image and it will appear here.</div>;
  }

  return (
    <div className="files-browser">
      {confirmDialog}
      <div className="files-header">
        <span className="files-count">{files.length} image{files.length !== 1 ? "s" : ""}</span>
        <button className="btn-secondary btn-sm" onClick={fetchFiles}>Refresh</button>
      </div>
      <div className="files-grid">
        {files.map((f) => (
          <div key={f.s3Key} className="file-card">
            <div className="file-preview">
              <img src={f.previewUrl} alt={f.filename} loading="lazy" />
            </div>
            <div className="file-info">
              <div className="file-name" title={f.filename}>{f.filename}</div>
              <div className="file-meta">
                <span>{formatSize(f.size)}</span>
                <span>{formatDate(f.lastModified)}</span>
              </div>
            </div>
            <div className="file-actions">
              <a href={f.previewUrl} target="_blank" rel="noopener noreferrer" className="btn-icon" title="Open">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
              <button
                className="btn-icon btn-danger"
                onClick={() => handleDelete(f.s3Key)}
                disabled={deletingKey === f.s3Key}
                title="Delete"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GeneratedImages;
